import {
  assertValidApplicationDocument, createApplicationDocument, migrateApplicationDocument
} from "../domain/applicationDocument.js";

const databaseVersion = 1;
const storeName = "application";
const documentKey = "document";

export function createDocumentRepository({
  databaseName = "regulator",
  indexedDB = globalThis.indexedDB
} = {}) {
  if (!indexedDB) throw new Error("IndexedDB is unavailable in this browser.");
  let databasePromise;

  function database() {
    databasePromise ??= openDatabase(indexedDB, databaseName);
    return databasePromise;
  }

  return {
    async loadDocument() {
      const db = await database();
      const storedDocument = await requestResult(db.transaction(storeName).objectStore(storeName).get(documentKey));
      if (!storedDocument) {
        const document = createApplicationDocument();
        await this.saveDocument(document);
        return document;
      }
      const result = migrateApplicationDocument(normalizeLegacyDocument(storedDocument));
      if (result.migrated) await this.saveDocument(result.document);
      return result.document;
    },

    async saveDocument(document) {
      assertValidApplicationDocument(document);
      const db = await database();
      const transaction = db.transaction(storeName, "readwrite");
      transaction.objectStore(storeName).put(document, documentKey);
      await transactionComplete(transaction);
    },

    async close() {
      if (!databasePromise) return;
      const db = await databasePromise;
      db.close();
      databasePromise = undefined;
    }
  };
}

function normalizeLegacyDocument(document) {
  if (!Array.isArray(document?.properties)) return document;
  return {
    ...document,
    allocations: document.allocations ?? [],
    policies: document.policies ?? [],
    browserViews: document.browserViews ?? []
  };
}

function openDatabase(indexedDB, databaseName) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, databaseVersion);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(storeName)) {
        request.result.createObjectStore(storeName);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error("The Regulator database is blocked by another tab."));
  });
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionComplete(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error ?? new Error("Database transaction aborted."));
  });
}
